/*  BadgeUnlockAnimation — full-screen celebratory overlay shown when the
    user unlocks a new achievement. Nike Training-style: dark backdrop,
    conic-gradient glow around the trophy, radial particles, haptic on
    open, swipe-down to dismiss, "Condividi" CTA (Web Share API).

    Usage:
      const [unlocked, setUnlocked] = useState(null);
      <BadgeUnlockAnimation badge={unlocked} onClose={() => setUnlocked(null)} />
      // Trigger: setUnlocked({ id, label, description, color, icon }).
*/
import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Share2, Trophy, X } from "lucide-react";
import { hapticSuccess } from "@/lib/haptics";

export default function BadgeUnlockAnimation({ badge, onClose }) {
  useEffect(() => {
    if (badge) {
      // Haptic pulse the moment the sheet mounts.
      try { hapticSuccess?.(); } catch { /* silent */ }
      // Auto-dismiss after 6 seconds if the user doesn't tap anything.
      const t = setTimeout(() => onClose?.(), 6000);
      return () => clearTimeout(t);
    }
  }, [badge, onClose]);

  const share = async () => {
    if (!badge) return;
    const text = `Ho appena sbloccato "${badge.label}" su ORIO 🏆`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "ORIO", text, url: "https://orio.app" });
      } else {
        await navigator.clipboard?.writeText(`${text} — https://orio.app`);
      }
    } catch { /* silent */ }
  };

  return (
    <AnimatePresence>
      {badge && (
        <motion.div
          className="fixed inset-0 z-[2000] flex items-center justify-center px-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="badge-unlock-overlay"
          style={{
            background:
              "radial-gradient(80% 50% at 50% 50%, rgba(255,122,26,0.35) 0%, rgba(11,8,6,0.85) 55%, rgba(5,4,3,0.95) 100%)",
            backdropFilter: "blur(24px)",
          }}
          onClick={onClose}
        >
          {/* Radial particles */}
          {Array.from({ length: 22 }).map((_, i) => {
            const angle = (i / 22) * Math.PI * 2;
            const dx = Math.cos(angle) * 220;
            const dy = Math.sin(angle) * 220;
            return (
              <motion.span
                key={i}
                className="absolute h-1.5 w-1.5 rounded-full"
                style={{ background: badge.color || "#FF7A1A" }}
                initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
                animate={{ x: dx, y: dy, opacity: [0, 1, 0], scale: [0.4, 1.2, 0.4] }}
                transition={{ duration: 1.6, delay: 0.15, ease: "easeOut" }}
              />
            );
          })}

          <motion.div
            className="relative flex flex-col items-center text-center"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: "spring", damping: 18, stiffness: 220 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Trophy ring — conic gradient shimmer */}
            <motion.div
              className="relative h-40 w-40 rounded-full flex items-center justify-center"
              style={{
                background:
                  `conic-gradient(from 0deg, ${badge.color || "#FF7A1A"}, #F4B83C, ${badge.color || "#FF7A1A"}, #F4B83C, ${badge.color || "#FF7A1A"})`,
                padding: 5,
                boxShadow: `0 30px 60px -12px ${badge.color || "#FF7A1A"}88`,
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            >
              <div
                className="w-full h-full rounded-full flex items-center justify-center"
                style={{ background: "#0B0806" }}
              >
                <motion.div
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity }}
                >
                  <Trophy
                    className="h-16 w-16"
                    strokeWidth={2.1}
                    style={{ color: badge.color || "#FF7A1A" }}
                  />
                </motion.div>
              </div>
            </motion.div>

            <div
              className="mt-6 text-[10.5px] uppercase tracking-[0.28em] font-bold"
              style={{ color: badge.color || "#FF7A1A" }}
            >
              Traguardo sbloccato
            </div>
            <div
              className="mt-2 font-display text-3xl leading-tight text-white"
              style={{ fontWeight: 700, letterSpacing: "-0.01em" }}
            >
              {badge.label}
            </div>
            {badge.description && (
              <div className="mt-2 text-[13px] text-white/70 max-w-xs leading-snug">
                {badge.description}
              </div>
            )}

            <div className="mt-6 flex items-center gap-2">
              <button
                onClick={share}
                data-testid="badge-share"
                className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full text-white font-semibold text-[13px] active:scale-95"
                style={{
                  background: `linear-gradient(135deg, ${badge.color || "#FF7A1A"} 0%, #F4B83C 100%)`,
                  boxShadow: `0 16px 36px -12px ${badge.color || "#FF7A1A"}99`,
                }}
              >
                <Share2 className="h-4 w-4" /> Condividi
              </button>
              <button
                onClick={onClose}
                data-testid="badge-close"
                className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full text-[13px] font-semibold text-white/85"
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.14)",
                }}
              >
                <X className="h-4 w-4" /> Chiudi
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
